import NextImage from "next/image";
import { useEffect, useState, useRef } from "react";
import { Box, Paper, Stack, useTheme, Container } from "@mui/material";
import { GameFilters } from "../GameFilters/GameFilters";
import OptionsMenu from "@/shared/modules/OptionsMenu/OptionsMenu";
import { GiftCardNavMenuItems } from "../PurchaseGiftCard/giftCard.navHelpers";
import { PurchaseCreditNavMenuItems } from "../PurchaseCredit/purchaseCredit.navHelpers";
import {
  extractUrlParams,
  createUrlFromNextRouter,
} from "@/global/utils/utils";
import { useAppStore } from "@/global/store";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { selectEventsSelected } from "@/global/store/data/purchaseSeats/events";
import { blurDataURL } from "@/global/common/blur-data-url";

export function AppHeader(props: {
  hideHamburgerMenu?: boolean;
  renderHamburgerMenu?: () => React.ReactNode;
}) {
  const theme = useTheme();
  const {
    images,
    teamSchema,
    defaultRoute,
    giftCardConfigs,
    purchaseCreditConfigs,
  } = useTeamConfig();
  const teamConfig = useTeamConfig();
  const { md: mdUp } = useCssMediaQuery("up");
  const { lg: lgDown, md: mdDown } = useCssMediaQuery("down");

  const hideRedeemItem = giftCardConfigs?.hideRedeemItem || false;
  const selectedEvents = useAppStore(selectEventsSelected);

  const [isHeaderSticky, setIsHeaderSticky] = useState(true);
  const [hasScrolledDown, setHasScrolledDown] = useState(false);
  const [startAnimation, setStartAnimation] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    function handleScroll() {
      if (window.scrollY > 100) return setHasScrolledDown(true);
      setHasScrolledDown(false);
    }

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (hasScrolledDown && selectedEvents && selectedEvents.length > 0) {
      setStartAnimation(true);
      timeoutRef.current = setTimeout(() => setIsHeaderSticky(false), 500);
    } else {
      setStartAnimation(false);
      setIsHeaderSticky(true);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [selectedEvents, hasScrolledDown]);

  function handleLogoClick() {
    window.location.href = createUrlFromNextRouter(defaultRoute, teamConfig);
  }

  return (
    <Paper
      id="flex-app-header"
      square={true}
      variant="outlined"
      sx={{
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        position: isHeaderSticky ? "sticky" : "unset",
        top: 0,
        backgroundColor: theme.palette.common.white,
        zIndex: 999,
        transition: "transform 0.5s ease",
        transform: startAnimation ? "translateY(-100%)" : "translateY(0)",
      }}
    >
      <Container maxWidth={"xl"}>
        <Stack
          py={2}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          spacing={mdDown ? 1 : 3}
        >
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            flex={lgDown ? "unset" : 1}
          >
            {images.logoImage ? (
              <Box
                width={`${extractUrlParams(images.logoImage).width}px`}
                height={`${extractUrlParams(images.logoImage).height}px`}
              >
                <NextImage
                  width={extractUrlParams(images.logoImage).width}
                  height={extractUrlParams(images.logoImage).height}
                  onClick={handleLogoClick}
                  alt={teamSchema}
                  src={images.logoImage}
                  style={{
                    cursor: "pointer",
                    width: `${extractUrlParams(images.logoImage).width}px`,
                    height: `${extractUrlParams(images.logoImage).height}px`,
                    objectFit: "contain",
                    borderRadius: "4px",
                  }}
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL={blurDataURL}
                />
              </Box>
            ) : null}

            {mdUp && images.logoNameImage && (
              <NextImage
                onClick={handleLogoClick}
                src={images.logoNameImage}
                width={130}
                height={64}
                alt={teamSchema}
                style={{ cursor: "pointer" }}
              />
            )}
          </Stack>

          <Stack alignItems={"center"} flexGrow={1}>
            <GameFilters />
          </Stack>
          <Stack alignItems={"flex-end"} flex={lgDown ? "unset" : 1}>
            {!props?.hideHamburgerMenu && props?.renderHamburgerMenu
              ? props.renderHamburgerMenu()
              : null}
            {!props?.hideHamburgerMenu && !props.renderHamburgerMenu ? (
              <OptionsMenu
                renderDynamicItems={() => {
                  return (
                    <Box>
                      {giftCardConfigs.isMenuGiftCardEnabled ? (
                        <GiftCardNavMenuItems hideReedemItem={hideRedeemItem} />
                      ) : null}

                      {purchaseCreditConfigs.showPurchaseCreditLinkInHomeMenu ? (
                        <PurchaseCreditNavMenuItems hideHome={true} />
                      ) : null}
                    </Box>
                  );
                }}
              />
            ) : null}

            {/* <SignInAndUp /> */}
          </Stack>
        </Stack>
      </Container>
    </Paper>
  );
}
